import {
  takeEvery,
  put,
  select,
  call,
  take,
  cancel,
  fork,
  all,
} from "typed-redux-saga/macro";

import {
  LOAD_CATEGORIES_BY_PRODUCT_IDS,
  DELETE_PRODUCTS_BY_CATEGORIES_ID,
  LoadCategoriesByProductIdsAction,
  DeleteProductByCategoriesAction,
  FETCH_CATEGORIES_BY_PRODUCT_IDS,
  DELETE_PRODUCT_BY_CATEGORIES,
  RESET_PRODUCT_REMOVE_STATE,
  RESET_PRODUCT_DELETED,
  DeleteProductByCategoriesIdSuccessAndErrorResponses,
  LockedProducts,
  BATCH_REMOVE_PRODUCTS_ASYNC,
  GET_BATCH_REMOVE_REQUEST_STATUS,
} from "./RemoveProductCategoriesTypes";

import { callApi, delay } from "../../utils/SagaUtils";

import { getData, postData } from "../../services/ApiService";
import { getUserId, getUserName } from "../../services/UserService";

import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../store-list/StoreListSelectors";

import { acquireEndpoint } from "../../utils/SmartMerchandiserAPI";
import { selectCurrentCatalogId } from "../catalog/CatalogSelectors";
import { addGlobalAlertState } from "../global-alert/GlobalAlertActions";
import {
  selectChildCategories,
  selectCurrentCategory,
  selectTopCategories,
} from "../category/CategorySelectors";
import { LoadCategoryDetailsById } from "../category/CategoryActions";
import { ProductSequenceData } from "../product-list/ProductListTypes";
import { MODAL_CLOSED } from "store/modal/ModalTypes";
import {
  productLoadingCancelled,
  unLockProduct,
  setPinnedProducts,
  updateProductListInState,
  updateProductSequenceList,
} from "store/product-list/ProductListActions";
import {
  selectLockedProducts,
  selectPinnedProductIds,
  selectProductListsFetched,
} from "store/product-list/ProductListSelectors";
import { reorderProductsList } from "utils/ProductUtil";

function* loadCategoriesByProductsId(action: LoadCategoriesByProductIdsAction) {
  try {
    yield* put({ type: FETCH_CATEGORIES_BY_PRODUCT_IDS.REQUEST });
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const localeCode = yield* select(selectCurrentLocale);
    const actionType = FETCH_CATEGORIES_BY_PRODUCT_IDS;
    const constName = Object.keys({
      FETCH_CATEGORIES_BY_PRODUCT_IDS,
    })[0].toString();

    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    const endpoint = acquireEndpoint(constName);
    yield* call(
      callApi,
      actionType,
      postData,
      action.payload,
      endpoint,
      headersObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_CATEGORIES_BY_PRODUCT_IDS.FAILURE,
      message: e.message,
    });
  }
}

function* updatePinnedProducts(
  deletedProductsWithCategoryId: Map<string, string[]>,
  currentCatalogId: string,
  unmanagedSupported: string | undefined,
  selectedTile?: string,
) {
  const pinnedProducts = yield* select(selectPinnedProductIds);
  for (const [
    categoryId,
    productIds,
  ] of deletedProductsWithCategoryId.entries()) {
    if (selectedTile) {
      for (const productId of productIds) {
        if (pinnedProducts.includes(productId)) {
          const newPinnedProducts = [...pinnedProducts];
          const selectedTileIndex = newPinnedProducts?.indexOf(selectedTile);
          if (selectedTileIndex !== -1) {
            newPinnedProducts.splice(selectedTileIndex, 1);
          }
          const deletedProductIndex = newPinnedProducts?.indexOf(productId);
          newPinnedProducts[deletedProductIndex] = selectedTile;
          yield* put(
            setPinnedProducts({
              pinnedProductIds: newPinnedProducts,
              sortOption: unmanagedSupported ?? "manual",
              catalogId: currentCatalogId,
              categoryId: categoryId,
            }),
          );
        } else if (pinnedProducts.includes(selectedTile)) {
          const newPinnedProducts = pinnedProducts.filter(
            (id) => id !== selectedTile,
          );
          yield* put(
            setPinnedProducts({
              pinnedProductIds: newPinnedProducts,
              sortOption: unmanagedSupported ?? "manual",
              catalogId: currentCatalogId,
              categoryId: categoryId,
            }),
          );
        }
      }
    } else {
      const updatedPinnedProducts = pinnedProducts?.filter(
        (id) => !productIds.includes(id),
      );
      if (updatedPinnedProducts?.length !== pinnedProducts?.length) {
        yield* put(
          setPinnedProducts({
            pinnedProductIds: updatedPinnedProducts,
            sortOption: unmanagedSupported ?? "manual",
            catalogId: currentCatalogId,
            categoryId: categoryId,
          }),
        );
      }
    }
  }
}

function* submitBatchRemove(
  categoryId: string,
  payload: { products: Array<{ productId: string }> },
  headersObj: Record<string, string | undefined>,
) {
  const actionType = DELETE_PRODUCT_BY_CATEGORIES;

  const asyncHeaders = {
    ...headersObj,
    "x-user-id": getUserId(),
    "x-user-name": getUserName(),
  };
  const asyncEndpoint = acquireEndpoint(
    "BATCH_REMOVE_PRODUCTS_FROM_CATEGORY_ASYNC",
    categoryId,
  );
  const submit = yield* call(
    callApi,
    BATCH_REMOVE_PRODUCTS_ASYNC,
    postData,
    payload,
    asyncEndpoint,
    asyncHeaders,
    { showGenericError: false },
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requestId = (submit as any)?.payload?.requestId;
  if (
    !submit ||
    submit.type !== BATCH_REMOVE_PRODUCTS_ASYNC.SUCCESS ||
    !requestId
  ) {
    yield* put({ type: actionType.FAILURE });
    return { type: actionType.FAILURE, payload: {} };
  }

  const statusEndpoint = acquireEndpoint(
    "GET_BATCH_REMOVE_REQUEST_STATUS",
    requestId,
  );
  const maxAttempts = 120; // 120 x 5s = up to 10 minutes
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    yield* call(delay, 5000);
    const res = yield* call(
      callApi,
      GET_BATCH_REMOVE_REQUEST_STATUS,
      getData,
      null,
      statusEndpoint,
      asyncHeaders,
      { showGenericError: false },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const record = (res as any)?.payload;
    const statusCode = record?.response?.statusCode;
    const status = record?.status;
    if (statusCode && statusCode <= 204) {
      yield* put({ type: actionType.SUCCESS });
      return {
        type: actionType.SUCCESS,
        payload: record?.response?.body ?? {},
      };
    }
    if ((statusCode && statusCode >= 400) || status === "FAILED") {
      yield* put({ type: actionType.FAILURE });
      return {
        type: actionType.FAILURE,
        payload: record?.response?.body ?? {},
      };
    }
  }
  yield* put({ type: actionType.FAILURE });
  return { type: actionType.FAILURE, payload: {} };
}

function* DeleteProductByCategories(action: DeleteProductByCategoriesAction) {
  try {
    const categoryIds = action.payload.categoryIds;
    const productIds = action.payload.productIds;
    const childCategories = yield* select(selectChildCategories);
    const topCategories = yield* select(selectTopCategories);
    const isSubstituteProduct = action.payload?.isSubstituteProduct ?? true;
    const filteredLockedProducts = action.payload?.lockedProducts ?? [];
    const unmanagedSupported = action.payload?.unmanagedSupported;
    const isFromSubstitution = action.payload?.isFromSubstitution ?? false;
    const selectedTile = action.payload?.selectedTile;
    const storeId = yield* select(selectCurrentStoreId);
    const catalogId = yield* select(selectCurrentCatalogId);
    const localeCode = yield* select(selectCurrentLocale);
    const currentCategoryId = yield* select(selectCurrentCategory);
    const actionType = DELETE_PRODUCT_BY_CATEGORIES;
    const successAndErrorResponses = {
      success: [],
      errors: [],
    } as DeleteProductByCategoriesIdSuccessAndErrorResponses;

    const currentStoreId = yield* select(selectCurrentStoreId);
    const currentCatalogId = yield* select(selectCurrentCatalogId);

    if (storeId === null) {
      throw new Error("Missing storeId");
    }
    if (localeCode === null) {
      throw new Error("Missing localeCode");
    }

    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };

    for (const catId of categoryIds) {
      const payload = {
        products: productIds.map((productId) => ({ productId })),
      };

      const result = yield* call(
        submitBatchRemove,
        catId,
        payload,
        headersObj,
      );

      if (result && result.type === actionType.SUCCESS) {
        const responsePayload = (result as any).payload ?? {};
        const removed: Array<{ productId: string }> = Array.isArray(
          responsePayload.removed,
        )
          ? responsePayload.removed
          : productIds.map((productId) => ({ productId }));
        const errors: Array<{ productId: string; reason?: string }> =
          Array.isArray(responsePayload.errors) ? responsePayload.errors : [];

        for (const entry of removed) {
          if (currentCategoryId === catId) {
            yield* put({
              type: DELETE_PRODUCT_BY_CATEGORIES.DONE,
              payload: { productId: entry.productId, categoryId: catId },
            });
          }
          successAndErrorResponses.success.push({
            ...(result as any),
            productId: entry.productId,
            categoryId: catId,
            type: actionType.SUCCESS,
          });
        }
        for (const entry of errors) {
          successAndErrorResponses.errors.push({
            ...(result as any),
            productId: entry.productId,
            categoryId: catId,
            reason: entry.reason,
            type: actionType.FAILURE,
          });
        }
      } else {
        // Whole-batch failure — mark every product in this category errored.
        for (const prodId of productIds) {
          successAndErrorResponses.errors.push({
            ...(result as any),
            productId: prodId,
            categoryId: catId,
          });
        }
      }
    }

    const deletedProducts: string[] = [];
    const deletedProductsWithCategoryId = new Map<string, string[]>();
    let deletedProductCount: number = 0;
    const apiSuccessResults = successAndErrorResponses.success;
    for (const successResponse of apiSuccessResults) {
      const productId = successResponse["productId"];
      const categoryId = successResponse["categoryId"];
      if (deletedProducts.length > 0) {
        if (!deletedProducts.includes(productId)) {
          deletedProducts.push(productId);
          deletedProductCount++;
        }
        if (deletedProductsWithCategoryId.has(categoryId)) {
          deletedProductsWithCategoryId.get(categoryId)?.push(productId);
        } else {
          deletedProductsWithCategoryId.set(categoryId, [productId]);
        }
      } else {
        deletedProducts.push(productId);
        deletedProductsWithCategoryId.set(categoryId, [productId]);
        deletedProductCount++;
      }
    }
    if (deletedProductsWithCategoryId.size > 0) {
      yield* call(
        updatePinnedProducts,
        deletedProductsWithCategoryId,
        currentCatalogId,
        unmanagedSupported,
        isFromSubstitution ? selectedTile : undefined,
      );
      yield* call(updateProductsList, currentCatalogId, filteredLockedProducts);
    }
    const errorProducts: string[] = [];
    const errorCategories: string[] = [];
    const apiErrorResults = successAndErrorResponses.errors;
    for (const errorResponse of apiErrorResults) {
      const errProductId = errorResponse["productId"];
      const errCategoryId = errorResponse["categoryId"];
      if (errorProducts.length > 0) {
        if (!errorProducts.includes(errProductId)) {
          errorProducts.push(errProductId);
        }
      } else {
        errorProducts.push(errProductId);
      }
      if (!errorCategories.includes(errCategoryId)) {
        errorCategories.push(errCategoryId);
      }
    }

    if (
      successAndErrorResponses.success.length > 0 &&
      successAndErrorResponses.errors.length === 0
    ) {
      if (isSubstituteProduct) {
        yield* put(
          addGlobalAlertState({
            alertsProps: [
              {
                descriptor: {
                  id: "removeProductCategoriesOperations.productDeleteSuccess",
                  defaultMessage: "Removed {productCount} products",
                },
                variables: {
                  productCount: `${deletedProductCount}`,
                },
                severity: "success",
                variant: "standard",
              },
            ],
          }),
        );
      }
    } else if (
      successAndErrorResponses.errors.length > 0 &&
      successAndErrorResponses.success.length === 0
    ) {
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "removeProductCategoriesOperations.productDeleteFailure",
                defaultMessage: "Unable to Remove Products",
              },
              severity: "error",
              variant: "standard",
            },
          ],
        }),
      );
    } else if (
      successAndErrorResponses.success.length > 0 &&
      successAndErrorResponses.errors.length > 0
    ) {
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "removeProductCategoriesOperations.productDeleteSuccessAndFailure",
                defaultMessage:
                  "Removed {productCount} products. Failed to remove products with id: {productIds} from {categoryIds}",
              },
              variables: {
                productCount: `${deletedProductCount}`,
                productIds: `${errorProducts}`,
                categoryIds: `${errorCategories}`,
              },
              severity: "warning",
              variant: "standard",
            },
          ],
        }),
      );
    }

    const categoryIdsToReload = Array.from(
      new Set(
        successAndErrorResponses.success
          .filter(
            (s) =>
              topCategories.some((c) => c.categoryId === s.categoryId) ||
              Object.values(childCategories).some((c) =>
                c.childCategories.some((cc) => cc.categoryId === s.categoryId),
              ),
          )
          .map((s) => s.categoryId),
      ),
    );

    yield* all(
      categoryIdsToReload.map((c) =>
        put(LoadCategoryDetailsById(catalogId, c, localeCode, storeId)),
      ),
    );

    if (deletedProducts.length > 0) {
      const payload = {
        storeId: currentStoreId,
        catalogId: currentCatalogId,
        productIds: deletedProducts,
        localeCode,
      };
      yield* put({
        payload,
        type: LOAD_CATEGORIES_BY_PRODUCT_IDS,
      });
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: DELETE_PRODUCT_BY_CATEGORIES.FAILURE,
      message: e.message,
    });
  }
}

function* updateProductsList(
  catalogId: string,
  removeLockForProducts: LockedProducts[],
) {
  try {
    const productsList = yield* select(selectProductListsFetched);
    const categoryId = yield* select(selectCurrentCategory);
    const lockedProductsInCategory = yield* select(
      selectLockedProducts,
      categoryId,
      catalogId,
    );

    const lockedProductsOutOfPosition = productsList.filter(
      (product: ProductSequenceData, currentIndex: number) => {
        // Find if this product is locked
        const lockedProduct = lockedProductsInCategory.find(
          (lock) => lock.productId === product.productId,
        );

        // If product is locked, check if its current position matches locked position
        return lockedProduct && currentIndex + 1 !== lockedProduct.position;
      },
    );

    const lockedProductsBeyondLength = lockedProductsInCategory.filter(
      (lock) => lock.position > productsList.length,
    );
    removeLockForProducts.push(...lockedProductsBeyondLength);
    if (lockedProductsOutOfPosition.length > 0) {
      const pinnedProducts = yield* select(selectPinnedProductIds);
      const updatedProductSequenceList = reorderProductsList(
        productsList,
        lockedProductsInCategory,
        pinnedProducts,
      );
      yield* put(
        updateProductListInState({
          products: updatedProductSequenceList,
          isSaveAction: true,
        }),
      );
      const productIdsArray = updatedProductSequenceList?.map((product) => {
        return product.productId;
      });
      const payload = {
        categoryId: categoryId,
        productIds: productIdsArray,
        isFromProductDeleteOperation: true,
      };
      yield* put(updateProductSequenceList(payload));
    }
    if (removeLockForProducts.length > 0)
      yield* put(unLockProduct({ unlockedProducts: removeLockForProducts }));
  } catch (e: any) {
    console.error(e);
  }
}

function* resetProductRemoveState() {
  try {
    yield* put({ type: RESET_PRODUCT_REMOVE_STATE });
  } catch (e: any) {
    console.error(e);
  }
}

export function* watchResetProductRemoveState() {
  yield* takeEvery(RESET_PRODUCT_DELETED, resetProductRemoveState);
}

export function* watchLoadCategoriesByProductIds() {
  yield* takeEvery(LOAD_CATEGORIES_BY_PRODUCT_IDS, loadCategoriesByProductsId);
}

export function* watchDeleteProductByCategories() {
  yield* takeEvery(DELETE_PRODUCTS_BY_CATEGORIES_ID, DeleteProductByCategories);
}

export function* watchCloseRemoveProductModal() {
  let task;
  while (true) {
    const action = yield* take(MODAL_CLOSED);
    if (task) {
      yield* cancel(task); // Cancel previous loading task if it's inflight
      yield* put(productLoadingCancelled());
    }
    task = yield* fork(
      loadCategoriesByProductsId,
      action as LoadCategoriesByProductIdsAction,
    ); // Start a new product loading task
  }
}
